import { useCallback, useMemo, useState } from 'react'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import { sendCommand } from '../lib/extension'
import { useAuth } from '../lib/auth'
import { useDebouncedValue } from './useDebounced'

export interface Connection {
  name: string
  headline: string
  profileUrn: string
  profileImage: string | null
  group?: string
}

const PAGE_SIZE = 100
const SEARCH_DEBOUNCE = 300

// Query keys must be user-scoped. The data comes from the extension (LinkedIn
// session for the currently-logged-in user), not Supabase — there's no RLS
// backstop. Without the user id in the key, signing in as a different user
// (or the brief cache overlap during sign-out/in) shows User A's connections
// to User B.
export const recentConnectionsKey = (userId: string | undefined): QueryKey =>
  ['connections', 'recent', userId ?? 'anon']
export const peerSearchKey = (userId: string | undefined, keywords: string): QueryKey =>
  ['connections', 'peerSearch', userId ?? 'anon', keywords]

interface ConnectionsPage {
  connections: Connection[]
}

export function useConnections() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const userId = user?.id

  // ─── Recent connections (paginated) ──────────────────────────────────────
  const recent = useInfiniteQuery<ConnectionsPage>({
    queryKey: recentConnectionsKey(userId),
    enabled: !!userId,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const start = pageParam as number
      const result = await sendCommand<{ connections: Connection[] }>('FETCH_RECENT_CONNECTIONS', {
        count: PAGE_SIZE,
        start,
      })
      return { connections: Array.isArray(result?.connections) ? result.connections : [] }
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.connections.length < PAGE_SIZE) return undefined
      return allPages.reduce((sum, p) => sum + p.connections.length, 0)
    },
  })

  const connections = useMemo<Connection[]>(
    () => (recent.data?.pages ?? []).flatMap((p) => p.connections),
    [recent.data],
  )

  // ─── Peer search (debounced) ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const trimmedSearch = searchQuery.trim()
  const debouncedRaw = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE)
  const debouncedQuery = trimmedSearch.length === 0 ? '' : debouncedRaw

  const search = useQuery<Connection[]>({
    queryKey: peerSearchKey(userId, debouncedQuery),
    enabled: !!userId && debouncedQuery.trim().length > 0,
    // Peer search results are effectively immutable for a given query
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const result = await sendCommand<{ peers: Connection[] }>('FETCH_PEER_SEARCH', {
        keywords: debouncedQuery,
        count: 50,
      })
      return Array.isArray(result?.peers) ? result.peers : []
    },
  })

  const searchResults = search.data ?? []
  // While the debounced query is being built up, show the spinner so the UI
  // feels responsive (original behaviour).
  const searchLoading = searchQuery.trim().length > 0 && (debouncedQuery !== searchQuery || search.isPending)

  const displayList = useMemo(
    () => (searchQuery.trim() ? searchResults : connections),
    [searchQuery, searchResults, connections],
  )

  const clearSearch = useCallback(() => {
    setSearchQuery('')
    // debouncedQuery is derived via useDebouncedValue; clearing searchQuery
    // short-circuits it to '' on the next render via the trimmed length check.
  }, [])

  const loadMore = useCallback(() => {
    if (recent.isFetchingNextPage || !recent.hasNextPage) return
    recent.fetchNextPage()
  }, [recent])

  // Optimistically prepend a newly-accepted invite to the recent-connections
  // cache. Mutates the first page of the infinite-query data.
  const addConnection = useCallback(
    (conn: Connection) => {
      qc.setQueryData<{ pages: ConnectionsPage[]; pageParams: unknown[] }>(recentConnectionsKey(userId), (prev) => {
        if (!prev || prev.pages.length === 0) {
          return { pages: [{ connections: [{ ...conn, group: 'Today' }] }], pageParams: [0] }
        }
        const first = prev.pages[0]
        if (first.connections.some((c) => c.profileUrn === conn.profileUrn)) return prev
        const nextFirst = { connections: [{ ...conn, group: 'Today' }, ...first.connections] }
        return { ...prev, pages: [nextFirst, ...prev.pages.slice(1)] }
      })
    },
    [qc, userId],
  )

  return {
    connections,
    loading: recent.isPending,
    hasMore: !!recent.hasNextPage,
    paginating: recent.isFetchingNextPage,
    searchQuery,
    setSearchQuery,
    searchLoading,
    displayList,
    clearSearch,
    loadMore,
    addConnection,
  }
}
